import { Artboard, File } from '@sketch-hq/sketch-prototype-player'
import groupBy from 'lodash.groupby'
import { ImageToPreload, ImageSrcSet } from '@sketch/components'

/**
 * Simplified type of the Artboard in the prototype manifest just to access the file.
 */
export type ArtboardToPreload = Pick<Artboard, 'files'>

/**
 * Convert a list of prototype artboards in the manifest format into a flat list of images
 * associated with those artboards containing a src and a srcset so we can preload them.
 */
export function getImagesToPreloadFromArtboardList(
  artboards: ArtboardToPreload[]
): ImageToPreload[] {
  return artboards.reduce((acc: ImageToPreload[], artboard) => {
    const imagesForArtboard = getImagesForArtboard(artboard)

    return acc.concat(imagesForArtboard)
  }, [])
}

/**
 * Convert a unique artboard into a list of images. Each artboard can have one or two different images (1 full or 1 scrollable and 1 fixed).
 * In the manifest, each image is also split into two versions (pixelRatio 1x and 2x) with two different s3 paths. We convert the multiple sources
 * into one image element with a srcset attribute handling the different pixel ratios.
 */
function getImagesForArtboard(artboard: ArtboardToPreload): ImageToPreload[] {
  // Group by image type to have images representing the same content but at different scale into one field.
  const imageFilesPerType = groupBy(artboard.files, 'type')

  const imagesTypes = Object.keys(imageFilesPerType)

  // Each image type correspond to 1 final image.
  const imagesToPreload: ImageToPreload[] = imagesTypes.map(type => {
    // Sort so we can easily find the lowest resolution version of the image to set it as the default "src" attribute.
    const imageFilesForTypeSortedByScale = imageFilesPerType[type].sort(
      (imageA, imageB) => {
        return imageA.scale > imageB.scale ? 1 : -1
      }
    )

    return {
      src: imageFilesForTypeSortedByScale[0].path,
      // All images representing the same image at different scales are "merged" into one using srcset attribute.
      srcset: buildSrcSetFromFiles(imageFilesForTypeSortedByScale),
    }
  })

  return imagesToPreload
}

/**
 * Build a SrcSet from a list of image files so we can automatically display
 * the correct best image depending on the screen pixelRatio of the user.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-srcset
 */
function buildSrcSetFromFiles(files: File[]): ImageSrcSet {
  return files.map(file => ({ path: file.path, scale: file.scale }))
}
